"use client";

import { c as _c } from "react/compiler-runtime";
import { CloseButton, Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";
import { CSSProperties, FC, Fragment, HTMLAttributes, PropsWithChildren, createContext, useContext } from "react";
import Icon from "../Icon";
export interface AppModalProps extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  width?: number | string;
  mobileFullscreen?: boolean;
  transparentBackdrop?: boolean;
  children?: any;
}
const AppModalContext = createContext<{
  mobileFullscreen: boolean;
}>({
  mobileFullscreen: false
});
export interface ModalTitleProps {
  closable?: boolean;
}
const ModalTitle: FC<PropsWithChildren<ModalTitleProps>> = t0 => {
  const $ = _c(10);
  let children;
  let closable;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      closable,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = closable;
    $[3] = props;
  } else {
    children = $[1];
    closable = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== closable) {
    t1 = closable && <CloseButton className="flex items-center text-neutral-675/60 hover:text-neutral-675 focusable ml-auto"><Icon type="Close" size="20" /></CloseButton>;
    $[4] = closable;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <DialogTitle {...props} className="z-10 px-3s py-3xs text-app-h3 border-b border-b-neutral-50 flex justify-between items-center sticky top-none gap bg-neutral-0">{children}{t1}</DialogTitle>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
const ModalDescription: FC<any> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Dialog.Description {...props} className="px-3s" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export interface ModalContentProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  padding?: boolean;
}
const ModalContent: FC<PropsWithChildren<ModalContentProps>> = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      padding: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const padding = t1 === undefined ? true : t1;
  const {
    mobileFullscreen
  } = useContext(AppModalContext);
  let t2;
  if ($[5] !== className || $[6] !== mobileFullscreen || $[7] !== padding) {
    t2 = clsx({
      "p-3s": padding
    }, mobileFullscreen && "flex-1 min-h-0 overflow-y-auto m:flex-none m:overflow-visible", className);
    $[5] = className;
    $[6] = mobileFullscreen;
    $[7] = padding;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== props || $[11] !== t2) {
    t3 = <div {...props} className={t2}>{children}</div>;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export interface ModalFooterProps {
  className?: string;
  neutralBackground?: boolean;
}
const ModalFooter: FC<PropsWithChildren<ModalFooterProps>> = t0 => {
  const $ = _c(7);
  const {
    children,
    className,
    neutralBackground: t1
  } = t0;
  const neutralBackground = t1 === undefined ? false : t1;
  const {
    mobileFullscreen
  } = useContext(AppModalContext);
  const t2 = mobileFullscreen ? "py-3xs border-t border-neutral-50 bg-neutral-0 m:border-none m:pt-none m:pb-3s" : "pb-3s";
  const t3 = neutralBackground && "bg-neutral-12 border-t border-neutral-50";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = clsx("px-3s flex gap-3xs justify-end items-center", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t4) {
    t5 = <div className={t4}>{children}</div>;
    $[4] = children;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const AppModal = t0 => {
  const $ = _c(51);
  let children;
  let className;
  let isOpen;
  let onOpenChange;
  let props;
  let t1;
  let t2;
  let width;
  if ($[0] !== t0) {
    ({
      isOpen,
      children,
      className,
      width,
      mobileFullscreen: t1,
      transparentBackdrop: t2,
      onOpenChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isOpen;
    $[4] = onOpenChange;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = width;
  } else {
    children = $[1];
    className = $[2];
    isOpen = $[3];
    onOpenChange = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    width = $[8];
  }
  const mobileFullscreen = t1 === undefined ? false : t1;
  const transparentBackdrop = t2 === undefined ? false : t2;
  let t3;
  if ($[9] !== mobileFullscreen || $[10] !== width) {
    t3 = mobileFullscreen ? width != null ? {
      "--app-modal-width": typeof width === "number" ? `${width}px` : width
    } as CSSProperties : {} : {
      maxWidth: "100%",
      width
    };
    $[9] = mobileFullscreen;
    $[10] = width;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const panelStyle = t3;
  let t4;
  if ($[12] !== className || $[13] !== mobileFullscreen || $[14] !== transparentBackdrop || $[15] !== width) {
    t4 = mobileFullscreen ? clsx("bg-neutral-0 flex flex-col shadow-3 overflow-hidden m:overflow-auto", "w-full max-w-full h-screen-safe rounded-none", "m:h-auto m:max-h-[calc(100vh-32px)] m:rounded-2xs m:max-w-full", width != null ? "m:w-[var(--app-modal-width)]" : "m:max-w-[700px]", className) : clsx("bg-neutral-0 flex flex-col overflow-auto max-h-[calc(100vh-48px)] rounded-2xs shadow-3", transparentBackdrop && "border border-neutral-50", className, {
      "max-w-[700px]": !width
    });
    $[12] = className;
    $[13] = mobileFullscreen;
    $[14] = transparentBackdrop;
    $[15] = width;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const panelClassName = t4;
  let t5;
  if ($[17] !== mobileFullscreen) {
    t5 = {
      mobileFullscreen
    };
    $[17] = mobileFullscreen;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== onOpenChange) {
    t6 = () => onOpenChange && onOpenChange(false);
    $[19] = onOpenChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const t7 = transparentBackdrop ? "bg-transparent" : "bg-neutral-875 bg-opacity-25";
  let t8;
  if ($[21] !== t7) {
    t8 = clsx("fixed w-screen h-screen flex justify-center items-center top-none left-none z-50", t7);
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <TransitionChild as={Fragment as any} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"><div className={t8} /></TransitionChild>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = mobileFullscreen ? "items-stretch m:items-center" : "items-center";
  let t11;
  if ($[25] !== t10) {
    t11 = clsx("fixed inset-none flex h-screen-safe justify-center z-50", t10);
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = !mobileFullscreen;
  let t13;
  if ($[27] !== mobileFullscreen || $[28] !== t12) {
    t13 = clsx("flex flex-1 justify-center overflow-hidden", {
      "p-none m:p-s": mobileFullscreen,
      "px-s pt-s pb-m": t12
    });
    $[27] = mobileFullscreen;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== children || $[31] !== panelClassName || $[32] !== panelStyle || $[33] !== props) {
    t14 = <DialogPanel style={panelStyle} className={panelClassName} {...props}>{children}</DialogPanel>;
    $[30] = children;
    $[31] = panelClassName;
    $[32] = panelStyle;
    $[33] = props;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <TransitionChild as={Fragment as any} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"><div className={t13}>{t14}</div></TransitionChild>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t11 || $[39] !== t15) {
    t16 = <div className={t11}>{t15}</div>;
    $[38] = t11;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16 || $[42] !== t6 || $[43] !== t9) {
    t17 = <Dialog onClose={t6}>{t9}{t16}</Dialog>;
    $[41] = t16;
    $[42] = t6;
    $[43] = t9;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== isOpen || $[46] !== t17) {
    t18 = <Transition appear={true} show={isOpen} as={Fragment as any}>{t17}</Transition>;
    $[45] = isOpen;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t18 || $[49] !== t5) {
    t19 = <AppModalContext.Provider value={t5}>{t18}</AppModalContext.Provider>;
    $[48] = t18;
    $[49] = t5;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default Object.assign(AppModal, {
  Title: ModalTitle,
  Description: ModalDescription,
  Content: ModalContent,
  Footer: ModalFooter
});